import React from 'react';
import * as THREE from 'three';
import { CONTAINER_LENGTH, CONTAINER_WIDTH, BASE_HEIGHT } from './gateStationGeometry';

export type GateStationLedMode = 'ready' | 'loading' | 'error' | 'idle';

type Props = {
  entry: boolean;
  shutterOpen?: number;
  active?: boolean;
  ledMode?: GateStationLedMode;
  door?: any;
  wallPanels?: any;
  towardNodeX?: number;
  towardNodeZ?: number;
};

function createStationSignTexture(entry: boolean): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 480;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new THREE.CanvasTexture(canvas);

  // Background
  ctx.fillStyle = '#060D1A';
  ctx.fillRect(0, 0, 1024, 480);

  // Outer border glow
  const primaryColor = entry ? '#10B981' : '#F59E0B';
  const glowColor = entry ? '#34D399' : '#FBBF24';

  ctx.strokeStyle = primaryColor;
  ctx.lineWidth = 14;
  ctx.strokeRect(10, 10, 1004, 460);

  // Inner subtle border
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 4;
  ctx.strokeRect(26, 26, 972, 428);

  // Top Accent Header Strip
  ctx.fillStyle = glowColor;
  ctx.fillRect(30, 30, 964, 16);

  // Left Direction Pill Badge
  ctx.fillStyle = primaryColor;
  const pillX = 50;
  const pillY = 90;
  const pillW = 200;
  const pillH = 80;
  const radius = 18;
  ctx.beginPath();
  if (typeof (ctx as any).roundRect === 'function') {
    (ctx as any).roundRect(pillX, pillY, pillW, pillH, radius);
  } else {
    ctx.rect(pillX, pillY, pillW, pillH);
  }
  ctx.fill();

  ctx.fillStyle = '#022C22';
  ctx.font = 'bold 44px "Segoe UI", Inter, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(entry ? '▼ INLET' : '▲ OUTLET', pillX + pillW / 2, pillY + pillH / 2);

  // Main Station Title
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '900 68px "Segoe UI", Inter, Arial, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(entry ? 'ENTRY STATION' : 'EXIT STATION', 280, 130);

  // Middle Glowing Divider
  ctx.fillStyle = glowColor;
  ctx.fillRect(50, 205, 924, 6);

  // Subtitle Operation
  ctx.fillStyle = glowColor;
  ctx.font = 'bold 36px "Segoe UI", Inter, Arial, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(
    entry ? 'INBOUND WAFER CARRIER · OHT DROP' : 'OUTBOUND WAFER CARRIER · OHT PICKUP',
    50,
    265
  );

  // Bottom Status / Standards Note
  ctx.fillStyle = '#94A3B8';
  ctx.font = '500 28px "Segoe UI", Inter, Arial, sans-serif';
  ctx.fillText(
    entry ? 'SEMI E84 / G85 AMHS VERTICAL TRANSFER DOCK' : 'SEMI E84 / G85 AMHS VERTICAL TRANSFER DOCK',
    50,
    335
  );

  // Bottom Status Bar
  ctx.fillStyle = entry ? '#065F46' : '#78350F';
  ctx.fillRect(50, 390, 924, 40);
  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 24px "Segoe UI", Inter, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(
    entry ? '● LOAD PORT 1 ACTIVE · READY FOR TRANSFER' : '● UNLOAD PORT 1 ACTIVE · READY FOR TRANSFER',
    512,
    410
  );

  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

function createCanopySignTexture(entry: boolean): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 200;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new THREE.CanvasTexture(canvas);

  ctx.fillStyle = '#060D1A';
  ctx.fillRect(0, 0, 1024, 200);

  const primaryColor = entry ? '#10B981' : '#F59E0B';
  ctx.strokeStyle = primaryColor;
  ctx.lineWidth = 10;
  ctx.strokeRect(6, 6, 1012, 188);

  ctx.fillStyle = '#FFFFFF';
  ctx.font = '900 68px "Segoe UI", Inter, Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(entry ? '▼ ENTRY STATION' : '▲ EXIT STATION', 512, 100);

  const tex = new THREE.CanvasTexture(canvas);
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

const stationSignTextureCache = new Map<boolean, THREE.CanvasTexture>();

function getStationSignTexture(entry: boolean): THREE.CanvasTexture {
  let tex = stationSignTextureCache.get(entry);
  if (!tex) {
    tex = createStationSignTexture(entry);
    stationSignTextureCache.set(entry, tex);
  }
  return tex;
}

const StationSignBadge: React.FC<{
  entry: boolean;
  position: [number, number, number];
  rotation?: [number, number, number];
}> = ({ entry, position, rotation = [0, 0, 0] }) => {
  const badgeTexture = React.useMemo(() => getStationSignTexture(entry), [entry]);
  const primaryColor = entry ? '#10B981' : '#F59E0B';

  return (
    <group position={position} rotation={rotation}>
      {/* 3D Sign Bevel Housing */}
      <mesh position={[0, 0, 0.04]} castShadow>
        <boxGeometry args={[5.6, 2.5, 0.08]} />
        <meshStandardMaterial color="#0A101D" metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Glowing Outer Border Rim */}
      <mesh position={[0, 0, 0.04]}>
        <boxGeometry args={[5.75, 2.65, 0.04]} />
        <meshStandardMaterial
          color={primaryColor}
          emissive={primaryColor}
          emissiveIntensity={0.8}
          toneMapped={false}
        />
      </mesh>
      {/* Crisp Canvas Signage Faceplate (Front Facing +Z) */}
      <mesh position={[0, 0, 0.085]} renderOrder={10}>
        <planeGeometry args={[5.5, 2.4]} />
        <meshBasicMaterial
          map={badgeTexture}
          transparent
          side={THREE.DoubleSide}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      {/* Crisp Canvas Signage Faceplate (Back Facing -Z) */}
      <mesh position={[0, 0, -0.005]} rotation={[0, Math.PI, 0]} renderOrder={10}>
        <planeGeometry args={[5.5, 2.4]} />
        <meshBasicMaterial
          map={badgeTexture}
          transparent
          side={THREE.DoubleSide}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
};

const CanopyBadge: React.FC<{
  entry: boolean;
  position: [number, number, number];
}> = ({ entry, position }) => {
  const canopyTexture = React.useMemo(() => createCanopySignTexture(entry), [entry]);
  const primaryColor = entry ? '#10B981' : '#F59E0B';

  return (
    <group position={position}>
      <mesh position={[0, 0, 0]} castShadow>
        <boxGeometry args={[5.8, 1.0, 0.16]} />
        <meshStandardMaterial color="#0A101D" metalness={0.8} roughness={0.25} />
      </mesh>
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[5.9, 1.1, 0.04]} />
        <meshStandardMaterial
          color={primaryColor}
          emissive={primaryColor}
          emissiveIntensity={0.8}
          toneMapped={false}
        />
      </mesh>
      {/* Front Faceplate */}
      <mesh position={[0, 0, 0.085]} renderOrder={10}>
        <planeGeometry args={[5.7, 0.95]} />
        <meshBasicMaterial
          map={canopyTexture}
          transparent
          side={THREE.DoubleSide}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
      {/* Back Faceplate */}
      <mesh position={[0, 0, -0.085]} rotation={[0, Math.PI, 0]} renderOrder={10}>
        <planeGeometry args={[5.7, 0.95]} />
        <meshBasicMaterial
          map={canopyTexture}
          transparent
          side={THREE.DoubleSide}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
};

/**
 * Clean empty open-frame cleanroom container enclosure for OHT Entry/Exit stations.
 * Features 4-sided open gantry structure with corner columns, base skid, and ceiling canopy.
 */
export const GateStationShell3D: React.FC<Props> = ({
  entry = true,
  active: _active = false,
}) => {
  const halfL = CONTAINER_LENGTH / 2; // 12.0
  const halfW = CONTAINER_WIDTH / 2;  // 8.0
  const colW = 0.28; // Very thin sleek dark-gray structural frame

  // Material Shading Definitions - Cleanroom Light-Gray Panels & Light-Gray Structural Frame
  const frameMat = {
    color: '#CBD5E1', // Cleanroom light-gray structural frame
    metalness: 0.55,
    roughness: 0.25,
    envMapIntensity: 1.25,
  };
  const skidMat = {
    color: '#94A3B8', // Neutral light-gray base skid
    metalness: 0.45,
    roughness: 0.35,
  };
  const chromeMat = {
    color: '#F8FAFC',
    metalness: 0.95,
    roughness: 0.1,
  };

  const COVER_HEIGHT = 20.0; // Height of lower solid enclosure
  const panelH = COVER_HEIGHT - BASE_HEIGHT;
  const panelCenterY = BASE_HEIGHT + panelH / 2;

  // Cleanroom Light-Gray Wall Panels
  const panelMat = {
    color: '#E2E8F0', // Light gray powder-coated industrial panel
    metalness: 0.25,
    roughness: 0.28,
    envMapIntensity: 0.9,
  };

  // Cleanroom Light-Gray Top Deck Plate
  const deckMat = {
    color: '#CBD5E1', // Neutral light-gray top deck plate
    metalness: 0.30,
    roughness: 0.28,
    envMapIntensity: 0.95,
  };

  return (
    <group name="GateStationContainer">
      {/* ──────────────── 1. HEAVY-DUTY BASE SKID & LEVELING PADS ──────────────── */}
      <mesh position={[0, BASE_HEIGHT / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[CONTAINER_LENGTH + 0.2, BASE_HEIGHT, CONTAINER_WIDTH + 0.2]} />
        <meshStandardMaterial {...skidMat} />
      </mesh>

      {/* 4 Corner Leveling Jack Pads */}
      {([-1, 1] as const).flatMap((sx) =>
        ([-1, 1] as const).map((sz) => (
          <group key={`foot-${sx}-${sz}`} position={[sx * (halfL - 0.2), 0.2, sz * (halfW - 0.2)]}>
            <mesh receiveShadow>
              <cylinderGeometry args={[1.2, 1.4, 0.4, 16]} />
              <meshStandardMaterial {...frameMat} />
            </mesh>
            <mesh position={[0, 0.35, 0]}>
              <cylinderGeometry args={[0.5, 0.5, 0.5, 12]} />
              <meshStandardMaterial {...chromeMat} />
            </mesh>
          </group>
        ))
      )}

      {/* Base Deck Floor Plate */}
      <mesh position={[0, BASE_HEIGHT + 0.1, 0]} receiveShadow>
        <boxGeometry args={[CONTAINER_LENGTH - 0.1, 0.2, CONTAINER_WIDTH - 0.1]} />
        <meshStandardMaterial color="#94A3B8" metalness={0.45} roughness={0.35} />
      </mesh>

      {/* ──────────────── 2. LOWER CABINET CORNER COLUMNS (Y = 0 TO 20) ──────────────── */}
      {([-1, 1] as const).flatMap((sx) =>
        ([-1, 1] as const).map((sz) => (
          <mesh
            key={`col-${sx}-${sz}`}
            position={[sx * (halfL - colW / 2), COVER_HEIGHT / 2, sz * (halfW - colW / 2)]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[colW, COVER_HEIGHT, colW]} />
            <meshStandardMaterial {...frameMat} />
          </mesh>
        ))
      )}

      {/* ──────────────── 3. LOWER SOLID ENCLOSURE (COVERED UP TO Y=20) ──────────────── */}
      {/* Front & Rear Lower Solid Walls (±X Faces) */}
      {([-1, 1] as const).map((sx) => (
        <mesh
          key={`lower-wall-x-${sx}`}
          position={[sx * (halfL - 0.1), panelCenterY, 0]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[0.2, panelH, CONTAINER_WIDTH - colW * 2]} />
          <meshStandardMaterial {...panelMat} />
        </mesh>
      ))}

      {/* Side Lower Solid Walls (±Z Faces) */}
      {([-1, 1] as const).map((sz) => (
        <mesh
          key={`lower-wall-z-${sz}`}
          position={[0, panelCenterY, sz * (halfW - 0.1)]}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[CONTAINER_LENGTH - colW * 2, panelH, 0.2]} />
          <meshStandardMaterial {...panelMat} />
        </mesh>
      ))}

      {/* ──────────────── 3b. PROMINENT ENTRY / EXIT STATION IDENTIFICATION BADGES ──────────────── */}
      {/* Front Face (+Z) Badge */}
      <StationSignBadge
        entry={entry}
        position={[0, 11.5, halfW - 0.02]}
        rotation={[0, 0, 0]}
      />

      {/* Rear Face (-Z) Badge */}
      <StationSignBadge
        entry={entry}
        position={[0, 11.5, -halfW + 0.02]}
        rotation={[0, Math.PI, 0]}
      />

      {/* Side Faces (±X) Badges */}
      <StationSignBadge
        entry={entry}
        position={[halfL - 0.02, 11.5, 0]}
        rotation={[0, Math.PI / 2, 0]}
      />
      <StationSignBadge
        entry={entry}
        position={[-halfL + 0.02, 11.5, 0]}
        rotation={[0, -Math.PI / 2, 0]}
      />

      {/* Top Illuminated Canopy Station Badges (Y = COVER_HEIGHT + 0.8) */}
      <CanopyBadge entry={entry} position={[0, COVER_HEIGHT + 0.8, halfW - 0.2]} />
      <CanopyBadge entry={entry} position={[0, COVER_HEIGHT + 0.8, -halfW + 0.2]} />

      {/* ──────────────── 4. VERY THIN DARK-GRAY TOP PERIMETER HEADER FRAME (Y = COVER_HEIGHT) ──────────────── */}
      {/* Front & Rear Header Beams (±Z Edges) */}
      <mesh position={[0, COVER_HEIGHT, -halfW + colW / 2]} castShadow receiveShadow>
        <boxGeometry args={[CONTAINER_LENGTH, 0.24, colW]} />
        <meshStandardMaterial {...frameMat} />
      </mesh>
      <mesh position={[0, COVER_HEIGHT, halfW - colW / 2]} castShadow receiveShadow>
        <boxGeometry args={[CONTAINER_LENGTH, 0.24, colW]} />
        <meshStandardMaterial {...frameMat} />
      </mesh>
      {/* Left & Right Header Beams (±X Edges) */}
      <mesh position={[-halfL + colW / 2, COVER_HEIGHT, 0]} castShadow receiveShadow>
        <boxGeometry args={[colW, 0.24, CONTAINER_WIDTH - colW * 2]} />
        <meshStandardMaterial {...frameMat} />
      </mesh>
      <mesh position={[halfL - colW / 2, COVER_HEIGHT, 0]} castShadow receiveShadow>
        <boxGeometry args={[colW, 0.24, CONTAINER_WIDTH - colW * 2]} />
        <meshStandardMaterial {...frameMat} />
      </mesh>

      {/* ──────────────── 5. TOP COVER PLATE & LIFTER SHAFT APERTURE (INSIDE SILVER FRAME) ──────────────── */}
      {/* Surrounding Top Deck Plates cleanly recessed inside the frame */}
      {/* Left Wing Plate (+X) */}
      <mesh position={[7.4, COVER_HEIGHT - 0.05, 0]} castShadow receiveShadow>
        <boxGeometry args={[7.6, 0.3, CONTAINER_WIDTH - colW * 2]} />
        <meshStandardMaterial {...deckMat} />
      </mesh>
      {/* Right Wing Plate (-X) */}
      <mesh position={[-7.4, COVER_HEIGHT - 0.05, 0]} castShadow receiveShadow>
        <boxGeometry args={[7.6, 0.3, CONTAINER_WIDTH - colW * 2]} />
        <meshStandardMaterial {...deckMat} />
      </mesh>
      {/* Solid Front Cover Plate (+Z of lifter shaft) */}
      <mesh position={[0, COVER_HEIGHT - 0.05, 3.05]} castShadow receiveShadow>
        <boxGeometry args={[7.2, 0.3, 9.3]} />
        <meshStandardMaterial {...deckMat} />
      </mesh>

      {/* Lifter Shaft Opening Trim Collar Bezel around Lifter at Z = -5.2 */}
      <mesh position={[3.6, COVER_HEIGHT + 0.02, -5.2]}>
        <boxGeometry args={[0.2, 0.16, 7.2]} />
        <meshStandardMaterial {...frameMat} />
      </mesh>
      <mesh position={[-3.6, COVER_HEIGHT + 0.02, -5.2]}>
        <boxGeometry args={[0.2, 0.16, 7.2]} />
        <meshStandardMaterial {...frameMat} />
      </mesh>
      <mesh position={[0, COVER_HEIGHT + 0.02, -1.6]}>
        <boxGeometry args={[7.4, 0.16, 0.2]} />
        <meshStandardMaterial {...frameMat} />
      </mesh>
      <mesh position={[0, COVER_HEIGHT + 0.02, -halfW + colW / 2]}>
        <boxGeometry args={[7.4, 0.16, 0.2]} />
        <meshStandardMaterial {...frameMat} />
      </mesh>
    </group>
  );
};

export default React.memo(GateStationShell3D);
